import * as React from 'react'

import { Textarea } from '@/components/ui/textarea'
import { findPromptSuggestion, PROMPT_SUGGESTIONS } from '@/lib/promptSuggestions'
import { cn } from '@/lib/utils'

type TextareaProps = React.ComponentProps<typeof Textarea>

interface Props extends Omit<TextareaProps, 'value' | 'onChange'> {
  value: string
  /**
   * Called when the user types OR accepts a suggestion. A synthetic
   * ChangeEvent is passed on Tab-accept so callers can keep using their
   * existing `(e) => onChange(e.target.value)` signature.
   */
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  /** Override the default suggestion pool (draws from videoTemplates + starters) */
  suggestions?: readonly string[]
  /** Hide the "Press Tab to insert" hint line. Default: false */
  hideHint?: boolean
}

/**
 * Drop-in replacement for shadcn Textarea that supports Tab-to-autocomplete.
 *
 * When the user's typed prefix matches a known phrase (case-insensitive),
 * a subtle hint appears below the textarea preview-ing the rest of the
 * phrase. Pressing Tab inserts the full phrase.
 */
export function PromptAutocompleteTextarea({
  value,
  onChange,
  onKeyDown,
  suggestions = PROMPT_SUGGESTIONS,
  hideHint = false,
  className,
  ...rest
}: Props) {
  const ref = React.useRef<HTMLTextAreaElement | null>(null)
  const [cursorAtEnd, setCursorAtEnd] = React.useState(true)

  const ghost = React.useMemo(
    () => (cursorAtEnd ? findPromptSuggestion(value, suggestions) : null),
    [value, suggestions, cursorAtEnd],
  )

  const checkCursor = React.useCallback(() => {
    const el = ref.current
    if (!el) return
    setCursorAtEnd(el.selectionStart === el.value.length && el.selectionEnd === el.value.length)
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab' && ghost) {
      e.preventDefault()
      const full = value + ghost
      // Synthesize a ChangeEvent-shaped object. The consumer only reads
      // e.target.value in practice, so this minimal shape is sufficient.
      const synthetic = {
        target: { value: full },
        currentTarget: { value: full },
      } as unknown as React.ChangeEvent<HTMLTextAreaElement>
      onChange(synthetic)
      // Position cursor at end of the inserted text on next paint
      requestAnimationFrame(() => {
        const el = ref.current
        if (el) {
          el.selectionStart = el.selectionEnd = full.length
        }
      })
      return
    }
    onKeyDown?.(e)
  }

  return (
    <div className="space-y-1.5">
      <Textarea
        {...rest}
        ref={ref}
        value={value}
        onChange={(e) => {
          onChange(e)
          // Cursor moves with the insertion; re-check after React flushes
          requestAnimationFrame(checkCursor)
        }}
        onKeyDown={handleKeyDown}
        onSelect={checkCursor}
        onClick={checkCursor}
        className={className}
      />
      {!hideHint && ghost && (
        <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground/80 pl-1">
          <kbd className="font-mono text-[10px] px-1 py-0 rounded border border-border bg-muted/40 shrink-0 mt-[1px]">
            Tab
          </kbd>
          <span className={cn('min-w-0 truncate')}>
            to insert:{' '}
            <span className="text-foreground/60">
              …{ghost.length > 90 ? ghost.slice(0, 90).trimEnd() + '…' : ghost}
            </span>
          </span>
        </div>
      )}
    </div>
  )
}
